import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import CategoryCard from '../components/home/CategoryCard';
import SaleProductSlider from '../components/SaleProductSlider';
import { callGetWebservice } from '../hooks/api';
import { setItemCategories } from '../store/reducers/general';
import { getBasePrice, getConvertedPrice, getQueryParam, printLogs } from '../utils/Utilities';
import PriceRangeSlider from '../components/PriceRangeSlider';
import SaleProductSliderGrid from '../components/SaleProductSliderGrid';
import SaleProductSliderList from '../components/SaleProductSliderList';
import defaultCategoryIcon from '../assets/img/category/all_category_icon.png';
import Skeleton from '../components/common/Skeleton';
import { Helmet } from 'react-helmet-async';

const ProductList = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const merchantSettings = useSelector((state) => state.general.merchantSettings);

    const [collapse, setCollapse] = useState('collapse');
    const [isCollapse, setIsCollapse] = useState(false);
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState({
        data: [],
        current_page: 1,
        last_page: 1
    });

    const [productType, setProductType] = useState('list-4');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState({
        categories: true,
        products: true
    });

    const initialCategoryId = getQueryParam(location, "category_id");
    const productSectionRef = useRef(null);

    const [filters, setFilters] = useState({
        'category_id': initialCategoryId,
        'sorting': 'name_asc',
        'min_price': null,
        'max_price': null,
    });

    const [selectedAttributes, setSelectedAttributes] = useState({});
    const [tempAttributes, setTempAttributes] = useState({});
    const [tempPrice, setTempPrice] = useState({ min: null, max: null });

    useEffect(() => {
        callGetWebservice('item-categories', {}, (response) => {
            handleItemCategoriesWebservice(response);
        });
    }, []);

    useEffect(() => {
        setPage(1);
        setHasMore(true);
        items(1, false);
    }, [filters, selectedAttributes]);

    useEffect(() => {
        const currentCategoryId = getQueryParam(location, "category_id");
        if (filters.category_id !== currentCategoryId) {
            setFilters(prev => ({ ...prev, category_id: currentCategoryId }));
            setTimeout(() => {
                productSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    }, [location, filters.category_id]);


    const handleItemCategoriesWebservice = (response) => {
        dispatch(setItemCategories(response.data));
        // Find if an "All" category exists in response, otherwise add it
        const originalAll = response.data?.find(c => c.id === 0);
        let resp;
        if (originalAll) {
            resp = response.data;
        } else {
            resp = [{ id: 0, name: 'All' }, ...response.data];
        }
        setCategories(resp);
        setLoading(prev => ({ ...prev, categories: false }));
    }

    const sortingChange = (e) => {
        const selectedOption = e.target.value;
        setFilters(prev => ({ ...prev, sorting: selectedOption }));
    };

    const getFiltersRequestPacket = (pageNumber = 1) => {
        let requestPacket = {
            page: pageNumber,
            sort_by: filters.sorting
        };

        if (filters.category_id && filters.category_id !== 0) {
            requestPacket.category_id = filters.category_id;
        }

        if (filters.min_price != null || filters.max_price != null) {
            filters.min_price != null && (requestPacket.min_price = getBasePrice(filters.min_price));
            filters.max_price != null && (requestPacket.max_price = getBasePrice(filters.max_price));
            requestPacket.sort_by = 'range';
        }


        // Map selected attributes to API format
        Object.keys(selectedAttributes).forEach(code => {
            if (selectedAttributes[code]) {
                requestPacket[code] = selectedAttributes[code];
                requestPacket.sort_by = 'other';
            }
        });

        return requestPacket;
    };

    const items = (pageNumber = 1, isLoadMore = false) => {
        if (!isLoadMore) setLoading(prev => ({ ...prev, products: true }));
        callGetWebservice('my-items', getFiltersRequestPacket(pageNumber), (response) => {
            handleItemsWebservice(response, isLoadMore);
        });
    }

    const handleItemsWebservice = (response, isLoadMore) => {
        const pagination = response.data;
        const items = pagination.data || [];

        if (isLoadMore) {
            setProducts(prev => ({
                ...pagination,
                data: [
                    ...(prev?.data || []),
                    ...items
                ]
            }));
        } else {
            setProducts({
                ...pagination,
                data: items
            });
        }

        setHasMore(pagination.current_page < pagination.last_page);
        setLoading(prev => ({ ...prev, products: false }));
    };


    const getCategoryName = () => {
        let categoryId = filters.category_id;
        if (!categoryId || categoryId === 0) return 'All Menu';
        const categoryIdInt = parseInt(categoryId, 10);
        return categories.find((i) => i.id === categoryIdInt)?.name || 'All Menu';
    }

    const priceChange = (data) => {
        setTempPrice({
            min: data.min,
            max: data.max
        });
    };

    const handleAttributeChange = (code, value) => {
        setTempAttributes(prev => ({
            ...prev,
            [code]: value
        }));
    };

    const applyFilters = () => {
        setFilters(prev => ({
            ...prev,
            min_price: tempPrice.min,
            max_price: tempPrice.max
        }));
        setSelectedAttributes(tempAttributes);
    };

    const clearFilters = () => {
        setFilters({
            'category_id': initialCategoryId,
            'sorting': 'name_asc',
            'min_price': null,
            'max_price': null,
        });
        setSelectedAttributes({});
        setTempAttributes({});
        setTempPrice({ min: null, max: null });
    };

    const productTypeClick = (val) => {
        setProductType(val);
    }

    const loadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        items(nextPage, true);
    };

    const handleCategoryClick = (id) => {
        setFilters(prev => ({ ...prev, category_id: id }));
        productSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const currentCategory = categories.find(c => c.id === (filters.category_id || 0));
    const filterConfig = merchantSettings?.config?.product_filter_json || [];
    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';

    return (
        <div className="product-list-page">
            <Helmet>
                <title>{getCategoryName() ? `${getCategoryName()} - Collection | Online Store` : 'Shop All Products | Online Store'}</title>
                <meta name="description" content={`Explore our complete collection of ${getCategoryName() || 'products'}. High quality, best prices, fast worldwide shipping.`} />
                {currentOrigin && <link rel="canonical" href={`${currentOrigin}/productlist`} />}
            </Helmet>
            <section className="bg-white py-5">
                <div className="container">
                    <div className="row justify-content-center mb-5">
                        <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 text-center">
                            <span className="text-primary font-heading fs-xs ls-2 text-uppercase d-block mb-2">Selection</span>
                            <h1 className="font-heading text-uppercase ls-1 display-5">Shop Categories</h1>
                        </div>
                    </div>

                    <div className="row justify-content-center g-4">
                        {loading.categories ? (
                            [1, 2, 3, 4, 5, 6].map((i) => (
                                <div key={i} className="col-xl-2 col-lg-3 col-md-4 col-sm-6 col-6">
                                    <Skeleton height="160px" borderRadius="16px" />
                                </div>
                            ))
                        ) : (
                            categories.map((category, index) => {
                                const isAll = category.id === 0;
                                const categoryData = isAll ? {
                                    id: 0,
                                    name: "All Menu",
                                    image: defaultCategoryIcon
                                } : category;
                                return (
                                    <div key={'category-' + index} className="col-xl-2 col-lg-3 col-md-4 col-sm-6 col-6" onClick={() => handleCategoryClick(category.id)}>
                                        <div className={`category-item-wrap ${filters.category_id === category.id ? 'active' : ''}`} style={{ cursor: 'pointer' }}>
                                            <CategoryCard category={categoryData} />
                                        </div>
                                    </div>
                                )
                            })
                        )}
                    </div>
                </div>
            </section>

            <section className="middle bg-light-soft py-5">
                <div className="container">
                    <div className="row">
                        <div className="col-xl-3 col-lg-3 col-md-12 col-sm-12">
                            <div className="shop-sidebar-wrap glass-card p-4 rounded-4 mb-4 shadow-sm border-0">
                                <h4 className="font-heading text-uppercase mb-4 ft-bold ls-1 border-bottom pb-3">Filters</h4>

                                {filterConfig.map((filter, index) => {
                                    if (filter.input === 'range' && currentCategory?.min_price > 0 && currentCategory?.max_price > 0) {
                                        const minPriceVal = getConvertedPrice(currentCategory?.min_price || 0);
                                        const maxPriceVal = getConvertedPrice(currentCategory?.max_price || 5000);
                                        if (minPriceVal === 0 && maxPriceVal === 0) return null;
                                        return (
                                            <div key={index} className="filter-group mb-5">
                                                <h5 className="filter-item-title mb-4">{filter.label}</h5>
                                                <PriceRangeSlider
                                                    filter={{
                                                        prices: {
                                                            min_price: minPriceVal,
                                                            max_price: maxPriceVal,
                                                            selected_min: tempPrice.min,
                                                            selected_max: tempPrice.max
                                                        }
                                                    }}
                                                    onChange={priceChange}
                                                />
                                                <button
                                                    className="btn btn-outline-dark rounded-pill ft-bold py-2 px-4 shadow-sm mt-3 text-uppercase ls-1 btn-sm"
                                                    style={{ borderWidth: '2px' }}
                                                    onClick={applyFilters}
                                                >
                                                    Filter
                                                </button>
                                            </div>
                                        );
                                    }

                                    if (filter.input === 'radio') {
                                        const isColor = filter.code?.toLowerCase().includes('color');
                                        return (
                                            <div key={index} className="filter-group mb-5">
                                                <h5 className="filter-item-title mb-4">{filter.label}</h5>
                                                <div className="attribute-options-list">
                                                    {filter.options?.map((opt, idx) => (
                                                        <div key={idx} className="custom-check-refined mb-3">
                                                            <input
                                                                style={{ display: 'none' }}
                                                                type="radio"
                                                                name={`filter-${filter.code}`}
                                                                id={`opt-${filter.code}-${idx}`}
                                                                checked={tempAttributes[filter.code] === opt.label}
                                                                onChange={() => handleAttributeChange(filter.code, opt.label)}
                                                            />
                                                            <label
                                                                className={`d-flex align-items-center gap-2 w-100 ${tempAttributes[filter.code] === opt.label ? 'active' : ''}`}
                                                                htmlFor={`opt-${filter.code}-${idx}`}
                                                                style={{ cursor: 'pointer' }}
                                                            >

                                                                <span className="ft-medium fs-md label-text">{opt.label}</span>
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                })}

                                <button
                                    className="btn btn-link w-100 text-muted mt-2 text-decoration-none small"
                                    onClick={clearFilters}
                                >
                                    Clear All
                                </button>
                            </div>
                        </div>

                        <div className="col-xl-9 col-lg-9 col-md-12 col-sm-12">
                            <div ref={productSectionRef} className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
                                <h2 className="font-heading text-uppercase fs-4 m-0 ft-bold">{getCategoryName()}</h2>
                                <div className="d-flex align-items-center gap-3">
                                    <div className="sort-dropdown-wrap position-relative">
                                        <select className="form-select modern-select rounded-pill px-4 border-0 shadow-sm" style={{ minWidth: '180px', height: '42px' }} onChange={sortingChange} value={filters.sorting}>
                                            <option value="name_asc">Sort by: Name A-Z</option>
                                            <option value="name_desc">Sort by: Name Z-A</option>
                                            <option value="price_asc">Price: Low to High</option>
                                            <option value="price_desc">Price: High to Low</option>
                                        </select>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button className={`btn-layout-toggle ${productType === 'list-4' ? 'active' : ''}`} onClick={() => productTypeClick('list-4')} title="Grid View">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
                                        </button>
                                        <button className={`btn-layout-toggle ${productType === 'list-12' ? 'active' : ''}`} onClick={() => productTypeClick('list-12')} title="List View">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-4 mb-5">
                                {loading.products ? (
                                    [1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                                        <div key={i} className={productType === 'list-4' ? "col-xl-4 col-lg-4 col-md-6 col-6" : "col-xl-12 col-lg-12 col-md-12 col-sm-12"}>
                                            <Skeleton height={productType === 'list-4' ? "380px" : "180px"} borderRadius="24px" />
                                        </div>
                                    ))
                                ) : products.data?.length > 0 ? (
                                    productType === 'list-4' ? (
                                        <SaleProductSliderGrid products={products.data} />
                                    ) : (
                                        <SaleProductSliderList products={products.data} />
                                    )
                                ) : (
                                    <div className="col-12">
                                        <div className="no-products-found glass-card p-5 text-center rounded-5 shadow-sm">
                                            <div className="empty-state-icon mb-4">
                                                <i className="fas fa-search-minus fa-4x text-muted opacity-25"></i>
                                            </div>
                                            <h3 className="ft-bold mb-2">No Products Found</h3>
                                            <p className="text-muted mb-4 mx-auto" style={{ maxWidth: '400px' }}>We couldn't find any products matching your current filters. Try adjusting your selection or search criteria.</p>
                                            <button className="btn btn-dark rounded-pill px-5 py-3 ft-bold shadow-sm" onClick={clearFilters}>
                                                Reset All Filters
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {hasMore && !loading.products && (
                                <div className="text-center mt-5">
                                    <button className="btn btn-outline-dark px-5 py-2 rounded-pill ft-bold" onClick={loadMore}>
                                        Load More
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

        </div>
    )
}

export default ProductList;